import { useEffect, useRef, useState } from 'react';
import { cn } from '~/lib/utils';
import { ScrollRevealHeadline } from '~/landing_page/components/ScrollRevealHeadline';
import landingType from '~/landing_page/styles/landingTypography.module.css';
import styles from './WhoItsForSection.module.css';
import { WALL_TILING } from './wallTiling.data';

/**
 * "Who it's for" — Figma 262:9538.
 *
 * The 14 background artifacts in the frame sit on an ellipse at an even 360/14 apart, so they are
 * one ring rather than a scatter. Here that ring is the inside wall of a sphere: the viewer sits at
 * the centre and the cards are tangent to the wall around them.
 *
 * The artifacts are real Deck covers in public/landing/who-its-for/Artifacts — see the folder's
 * SOURCES.md for how the set grows. Pass `artifactSrcs` to use a different set.
 */

const DEFAULT_ROLES = [
  'Founders pitching their next round',
  'PMs aligning the team on what’s next',
  'Designers making their case',
  'Engineers explaining the hard stuff',
  'Consultants on a client deadline',
] as const;

const ROLE_HOLD_MS = 2400;

/**
 * How many covers are in `public/landing/who-its-for/Artifacts` — `artifact_1.jpg` up to this.
 *
 * The full set: 48. If covers are ever added or removed, change this to match: every card on the wall
 * points at one of `artifact_1` … `artifact_<count>`, so a number above what is on disk draws broken
 * images, and a number below it simply leaves the newest ones off the wall.
 */
const ARTIFACT_COUNT = 48;

/** The real covers, tiled over the ~150 cards on the shell. See the folder's SOURCES.md. */
const DEFAULT_ARTIFACT_SRCS = Array.from(
  { length: ARTIFACT_COUNT },
  (_, i) => `/landing/who-its-for/Artifacts/artifact_${i + 1}.jpg`,
);

/**
 * FALLBACK ONLY — the wall normally uses `WALL_TILING`; see `coverFor`.
 *
 * The stride the wall walks the covers by: 17 wherever it can be, nudged up to the nearest number
 * that shares no factor with the set's size.
 *
 * Sharing no factor is what makes the walk visit every cover before repeating one. 17 was picked
 * against a set of 48, and on the way from 12 to 48 the set passes through 17 and 34, where a fixed
 * stride of 17 lands every card on the same cover — the whole wall one image.
 */
function strideFor(count: number): number {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  let stride = 17;
  while (count > 1 && gcd(stride, count) !== 1) stride += 1;
  return stride;
}

/**
 * A literal globe seen from the inside. The camera sits at perspective 700 while the shell has a
 * radius of 900, so the eye is genuinely inside the sphere rather than looking at it from outside.
 * Every artifact is tiled onto the inner wall and flipped to face the middle, which is what puts
 * the ones ahead of you square on, shears the ones at your left and right almost edge-on, and tips
 * the ones above and below back toward you. Nothing is skewed by hand; it all falls out of where
 * the card sits on the shell.
 *
 * Points are spread by the golden angle rather than in latitude rows, because rows visibly bunch up
 * at the poles and read as a grid the moment the globe turns.
 */
const SPHERE_RADIUS_PX = 900;
/*
 * 16:9, so a deck cover fills the card without `cover` having to crop anything off it.
 *
 * 15% up from 260x146 so the 48 covers can fill the wall without one showing twice at once (Beka,
 * 2026-09-15). At 260 a full view held up to 63 cards, more than there are covers, so repeats were
 * unavoidable whatever the order; at 299 the busiest view holds 39. Mirrored in `.sphereCard` and in
 * scripts/genWhoItsForTiling.mjs — change all three together, then re-run the script.
 */
const CARD_W = 299;
const CARD_H = 168;
/* On the shell, not on screen. Perspective shrinks the far wall to about 0.55, so 55 here lands as
   the ~30px gap that was asked for. */
const CARD_GAP = 55;
const DEG = 180 / Math.PI;

/**
 * Tiled in latitude rows rather than by a spiral, because the gap is the thing being specified and
 * rows make it exact: one step of (card + gap) along the shell is that arc divided by the radius,
 * and each row takes as many columns as its own (shorter) circumference allows. Poles are skipped,
 * where the rows would collapse into each other.
 */
/**
 * Every artifact faces the middle of the shell, and the eye sits just outside it. That combination
 * is what shows the concave far wall — the near half turns its back and is culled — so you get the
 * view from inside without the eye ever being crossed by a card. Putting the eye literally inside
 * cannot avoid that: one card always passes through the camera plane and projects to tens of
 * thousands of pixels, which is what turned the section solid black.
 */
const SPHERE_CARDS: string[] = [];
{
  const latStep = ((CARD_H + CARD_GAP) / SPHERE_RADIUS_PX) * DEG;
  for (let lat = -84; lat <= 84; lat += latStep) {
    const ringRadius = Math.cos(lat / DEG) * SPHERE_RADIUS_PX;
    const columns = Math.max(1, Math.round((2 * Math.PI * ringRadius) / (CARD_W + CARD_GAP)));
    for (let c = 0; c < columns; c += 1) {
      // Odd rows are offset by half a column so the tiling breaks its grid and reads as scattered.
      const yaw = (c / columns) * 360 + (Math.round(lat) % 2 === 0 ? 0 : 180 / columns);
      SPHERE_CARDS.push(
        `rotateY(${yaw.toFixed(2)}deg) rotateX(${(-lat).toFixed(2)}deg) ` +
          `translateZ(${SPHERE_RADIUS_PX}px) rotateY(180deg)`,
      );
    }
  }
}

export interface WhoItsForSectionProps {
  id?: string;
  className?: string;
  headline?: string;
  roles?: readonly string[];
  artifactSrcs?: readonly string[];
  webCtaLabel?: string;
  webCtaHref?: string;
  macCtaLabel?: string;
  macCtaHref?: string;
  /** `dark` is the landing v2 ground: same globe, the scrim, cards and ink in the dark ramp. */
  tone?: 'light' | 'dark';
}

export function WhoItsForSection({
  id,
  className,
  headline = 'Storytelling is everybody’s job.',
  roles = DEFAULT_ROLES,
  artifactSrcs = DEFAULT_ARTIFACT_SRCS,
  webCtaLabel = 'Continue in web',
  webCtaHref = '/sign-in',
  macCtaLabel = 'Download Mac app',
  macCtaHref = '/sign-in',
  tone = 'light',
}: WhoItsForSectionProps) {
  const stride = strideFor(artifactSrcs?.length ?? 0);
  /*
   * NO COVER TWICE IN ONE VIEW. `WALL_TILING` is a generated arrangement — every copy of a cover
   * placed far enough round the globe that no two are ever noticeably on screen together, at any
   * moment of its turn, in browser windows up to 1920x970. It only holds for the exact card geometry
   * and cover count it was generated against, so anything else (a different set passed in, or cards
   * changed without re-running scripts/genWhoItsForTiling.mjs) falls back to the stride.
   */
  const tilingFits =
    WALL_TILING.covers === artifactSrcs?.length &&
    WALL_TILING.cards === SPHERE_CARDS.length &&
    WALL_TILING.cardW === CARD_W &&
    WALL_TILING.cardH === CARD_H &&
    WALL_TILING.cardGap === CARD_GAP;
  const coverFor = (card: number): number =>
    tilingFits ? WALL_TILING.tiling[card] : (card * stride) % (artifactSrcs?.length || 1);
  // The first role is repeated at the end so the roll from last to first travels the same direction
  // as every other step. On landing there, the transition is switched off for one frame and the
  // track snaps back to the real first item, which the eye never catches.
  const [index, setIndex] = useState(0);
  const [snapping, setSnapping] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (roles.length <= 1) return;

    const clear = () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = null;
    };

    if (snapping) {
      timerRef.current = setTimeout(() => setSnapping(false), 20);
      return clear;
    }

    if (index === roles.length) {
      setIndex(0);
      setSnapping(true);
      return clear;
    }

    timerRef.current = setTimeout(() => setIndex((i) => i + 1), ROLE_HOLD_MS);
    return clear;
  }, [index, snapping, roles.length]);

  const rolled = [...roles, roles[0]];

  return (
    <section
      id={id}
      className={cn(styles.section, tone === 'dark' && styles.sectionDark, className)}
      data-name="WhoItsForSection"
    >
      {/* Globe, scrim and copy are all laid over the same box — the section itself — so the fade
          reaches white exactly at the section's edges. While the scrim lived inside the stage its
          gradient finished below the visible boundary, which is what left the hard cut. */}
      <div className={styles.sphereViewport} aria-hidden="true">
        <div className={styles.sphere}>
          {SPHERE_CARDS.map((transform, i) => {
            const src = artifactSrcs?.length ? artifactSrcs[coverFor(i)] : undefined;
            return (
              <div key={i} className={styles.sphereCard} style={{ transform }}>
                {src ? (
                  <img
                    className={styles.sphereCardImage}
                    src={src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.copyScrim} aria-hidden="true" />

      <div className={styles.stage}>
        <div className={cn(landingType.sectionInset, styles.container)}>
        <ScrollRevealHeadline className={cn(landingType.sectionHeadline, styles.headline)}>
          {headline}
        </ScrollRevealHeadline>

        <div className={styles.roleViewport}>
          <div
            className={cn(styles.roleTrack, snapping && styles.roleTrackSnapping)}
            /* Percentages here resolve against the track, which is every line stacked, so one step
               is 100% / lines rather than a flat 100%. Keeping it in percent means the mobile line
               height needs no separate maths. */
            style={{ transform: `translateY(calc(${-index} * 100% / ${rolled.length}))` }}
          >
            {rolled.map((role, i) => (
              <p key={`${role}-${i}`} className={styles.role}>
                {role}
              </p>
            ))}
          </div>
          {/* The roll is decorative repetition of one idea, so the live region reads only the
              first line rather than announcing a new role every 2.4s. */}
          <p className={styles.roleVisuallyHidden}>{roles[0]}</p>
        </div>

        <div className={styles.actions}>
          <a className={styles.webCta} href={webCtaHref}>
            <img className={styles.ctaIcon} src="/landing/hero/icon-web.svg" alt="" width={18} height={18} />
            {webCtaLabel}
          </a>
          <a className={styles.macCta} href={macCtaHref}>
            <img className={styles.ctaIcon} src="/landing/hero/icon-mac.svg" alt="" width={18} height={18} />
            {macCtaLabel}
          </a>
          </div>
        </div>
      </div>
    </section>
  );
}

WhoItsForSection.displayName = 'WhoItsForSection';
